import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOutIcon, SearchIcon, UsersIcon } from "lucide-react";

import { useAuth } from "../app/AuthContext";
import { Kbd } from "./ui/kbd";
import { Button } from "./ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Separator } from "./ui/separator";
import { SidebarTrigger } from "./ui/sidebar";
import { navigate } from "../lib/router";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface TopbarProps {
  /** Title shown when no breadcrumb is provided. */
  title?: string;
  subtitle?: string;
  /** Optional breadcrumb trail rendered left of the title. */
  breadcrumb?: BreadcrumbItem[];
}

export function Topbar({ title, subtitle, breadcrumb }: TopbarProps) {
  const [open, setOpen] = useState(false);

  // Listen for the global Cmd/Ctrl+K and "/" shortcuts so the command
  // palette is discoverable from anywhere in the dashboard.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const isTyping =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable;
      if (isTyping) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "/" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setOpen(true);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b bg-card px-4 md:gap-3 md:px-8">
      <SidebarTrigger id="sidebar-toggle" className="-ml-1" />
      <Separator orientation="vertical" className="h-6" />
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        {breadcrumb && breadcrumb.length > 0 ? (
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumb.map((item, idx) => {
                const isLast = idx === breadcrumb.length - 1;
                return (
                  <Fragment key={`${item.label}-${idx}`}>
                    {idx > 0 ? <BreadcrumbSeparator /> : null}
                    <BreadcrumbItem>
                      {item.to && !isLast ? (
                        <BreadcrumbLink render={<Link to={item.to}>{item.label}</Link>} />
                      ) : (
                        <BreadcrumbPage>{item.label}</BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                  </Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        ) : (
          <span className="truncate text-sm font-semibold tracking-tight text-foreground">
            {title}
          </span>
        )}
        {subtitle ? (
          <span className="truncate text-xs text-muted-foreground">
            {subtitle}
          </span>
        ) : null}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className="hidden h-9 min-w-56 justify-start gap-2 px-3 font-normal text-muted-foreground md:inline-flex"
          title="Open command palette"
          data-testid="command-trigger"
        >
          <SearchIcon />
          <span>Search</span>
          <span className="ml-auto inline-flex items-center gap-0.5">
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>
        </Button>
        <UserBadge />
      </div>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a command or search…" />
        <CommandList>
          <CommandEmpty>
            No commands yet — try one of the navigation entries below.
          </CommandEmpty>
          <CommandGroup heading="Navigate">
            <CommandItem
              value="navigate all projects"
              onSelect={() => {
                setOpen(false);
                navigate("/");
              }}
            >
              All projects
            </CommandItem>
            <CommandItem
              value="navigate new project"
              onSelect={() => {
                setOpen(false);
                navigate("/projects/new");
              }}
            >
              Create a new project
            </CommandItem>
          </CommandGroup>
          <CommandGroup heading="Coming soon">
            <CommandItem value="coming search reports" disabled>
              Search reports
            </CommandItem>
            <CommandItem value="coming global search" disabled>
              Global search
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </header>
  );
}

function UserBadge() {
  const { token, clear } = useAuth();
  const last4 = token ? token.slice(-4) : "";
  if (!token) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={clear}
      className="hidden gap-2 text-xs font-normal text-muted-foreground md:inline-flex"
      title="Sign out"
      aria-label={`Sign out admin session ending in ${last4}`}
    >
      <UsersIcon data-icon="inline-start" />
      <span>Admin <span className="font-mono text-foreground/70">…{last4}</span></span>
      <LogOutIcon data-icon="inline-end" />
    </Button>
  );
}
